/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useCallback, useRef } from 'react'
import { Typography, Button, Box } from '@mui/material'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import mimeTypes from 'mime'

import AuthContext from '../../contexts/AuthContext'
import createEventQuery from '../../graphql/mutation/createEvent'
import organizersQuery from '../../graphql/query/organizers'
import { useToast } from '../../contexts/ToastContext'
import axios from 'axios'

// Import components
import {
  EventDetailsForm,
  ShirtSection,
  TicketSection,
  FeatureToggles,
} from '../../components/create-event'

const CreateEventPage = () => {
  const formRef = useRef(null)
  const [eventNameEN, setEventNameEN] = useState('')
  const [organizer, setOrganizer] = useState('')
  const [shirts, setShirt] = useState([])
  const [slugShirt, setSlugShirt] = useState('')
  const [typeShirt, setTypeShirt] = useState('')
  const [imageShirt, setImageShirt] = useState('')
  const [tickets, setTickets] = useState([])
  const [ticketType, setTicketType] = useState('')
  const [dateTicket, setDateTicket] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [timeDate, setTimeDate] = useState(false)
  const [slugEvent, setSlugEvent] = useState('')
  const [codeEvent, setCodeEvent] = useState('')
  const [file, setFile] = useState({})
  const [QRSearch, setQRSearch] = useState(false)
  const [IDSearch, setIDSearch] = useState(false)
  const [uploadFace, setUploadFace] = useState(false)
  const [isFaceCheckin, setIsFaceCheckin] = useState(false)
  const [isShirtImage, setIsShirtImage] = useState(false)
  const [IDFace, setIDFace] = useState(false)
  const [print, setPrint] = useState(false)
  const [selfCheckin, setSelfCheckin] = useState(false)
  const [autoPickup, setAutoPickup] = useState(false)
  const navigate = useNavigate()
  const { user } = useContext(AuthContext)
  const { showSuccess, showError } = useToast()
  const reader = new FileReader()

  const addShirt = useCallback(() => {
    const newShirt = {
      id: `shirt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      slug: slugShirt,
      type: typeShirt,
      image: imageShirt,
    }
    setShirt((oldShirts) => [...oldShirts, newShirt])
    setSlugShirt('')
    setImageShirt('')
    setTypeShirt('')
  }, [slugShirt, typeShirt, imageShirt])

  const addTicketType = useCallback(() => {
    const newTicket = {
      id: `ticket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ticketType,
      dateTicket,
      startDate,
      endDate,
    }
    setTickets((oldTickets) => [...oldTickets, newTicket])
    setTicketType('')
    setDateTicket('')
    setStartDate('')
    setEndDate('')
  }, [ticketType, dateTicket, startDate, endDate])

  const removeShirt = useCallback((index) => {
    setShirt((oldShirts) => [...oldShirts.slice(0, index), ...oldShirts.slice(index + 1)])
  }, [])

  const removeTicket = useCallback((index) => {
    setTickets((oldTickets) => [...oldTickets.slice(0, index), ...oldTickets.slice(index + 1)])
  }, [])

  const { data } = useQuery(organizersQuery)
  const [createEvent] = useMutation(createEventQuery)

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate required fields (logo is optional)
    if (!eventNameEN || !organizer || !slugEvent || !codeEvent) {
      showError('Please fill in all required fields (Event Name, Organizer, Slug, Code)', 'Validation Error')
      return
    }

    if (!process.env.REACT_APP_GRAPHQL_SERVER) {
      showError('GraphQL server is not configured', 'Configuration Error')
      return
    }

    const createEventWithLogo = async (logoUrl) => {
      // `id` is a client-side key only; the GraphQL input types don't accept it
      const shirtsInput = shirts.map(({ id, ...shirt }) => shirt)
      const ticketsInput = tickets.map(({ id, ...ticket }) => ticket)
      const mutationResult = await createEvent({
        variables: {
          record: {
            name: eventNameEN,
            organizerId: organizer,
            slug: slugEvent,
            code: codeEvent,
            ...(logoUrl != null && logoUrl !== '' ? { logo: logoUrl } : {}),
            isFaceCheckin: isFaceCheckin,
            isShirtImage: isShirtImage,
            shirts: shirtsInput,
            tickets: ticketsInput,
            createdById: user._id,
            options: {
              search: { qrcode: QRSearch, idcard: IDSearch },
              face: { upload: uploadFace, idcard: IDFace },
              print,
              selfCheckin,
              autoPickup,
            },
          },
        },
      })
      if (mutationResult && mutationResult.data) {
        showSuccess('Event created successfully!', 'Success')
        setTimeout(() => navigate('/events'), 1500)
      } else {
        throw new Error('Event creation failed: No data returned')
      }
    }

    // No logo selected: create event without logo
    if (!file || !file.name) {
      try {
        await createEventWithLogo(null)
      } catch (error) {
        console.error('Error creating event:', error)
        const errorMessage =
          error.message ||
          error.graphQLErrors?.[0]?.message ||
          'Failed to create event. Please try again.'
        showError(errorMessage, 'Create Event Failed')
      }
      return
    }

    // Logo selected: require upload server and validate file
    if (!process.env.REACT_APP_UPLOAD_SERVER) {
      showError('Upload server is not configured', 'Configuration Error')
      return
    }

    try {
      // Validate file type before processing
      if (!file.type || !file.type.startsWith('image/')) {
        showError('Please select a valid image file (PNG, JPG, GIF, SVG)', 'Invalid File Type')
        return
      }

      // Validate file size (max 5MB)
      const maxFileSize = 5 * 1024 * 1024 // 5MB in bytes
      if (file.size > maxFileSize) {
        showError(
          `File size is too large. Maximum file size is 5MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`,
          'File Size Error'
        )
        return
      }

      if (file.size === 0) {
        showError('File is empty. Please select a valid file.', 'Invalid File')
        return
      }

      reader.readAsDataURL(file)
      reader.onload = async (event) => {
        try {
          // Get file extension from MIME type (same as editEventPage)
          const extType = mimeTypes.getExtension(file.type)
          const ext = mimeTypes.getType(extType)

          if (!ext) {
            showError(
              'Invalid file type. Please select a valid image file (PNG, JPG, GIF, SVG).',
              'Invalid File Type'
            )
            return
          }

          console.log('Uploading file:', {
            name: file.name,
            type: file.type,
            size: file.size,
            extType,
            ext,
            uploadServer: process.env.REACT_APP_UPLOAD_SERVER,
          })

          const requestBody = {
            ext: `${ext}`,
          }
          console.log('Upload request body:', requestBody)

          const response = await fetch(process.env.REACT_APP_UPLOAD_SERVER, {
            method: 'POST',
            body: JSON.stringify(requestBody),
            headers: {
              'Content-Type': 'application/json',
            },
          })

          // Read response as text first (can be parsed as JSON later if needed)
          const responseText = await response.text()

          if (!response.ok) {
            // Try to parse as JSON first, fallback to text
            let errorMessage = `Upload server error (${response.status} ${response.statusText})`
            let errorDetails = null

            try {
              const errorData = JSON.parse(responseText)
              errorMessage =
                errorData.message || errorData.error || errorData.details || errorMessage
              errorDetails = errorData
              console.error('Upload server error details:', {
                status: response.status,
                statusText: response.statusText,
                error: errorData,
                requestBody: requestBody,
                fileInfo: {
                  name: file.name,
                  type: file.type,
                  size: file.size,
                },
              })
            } catch (e) {
              // If not JSON, use text directly
              if (responseText) {
                errorMessage = `${errorMessage}: ${responseText.substring(0, 200)}`
                console.error('Upload server error text:', {
                  status: response.status,
                  statusText: response.statusText,
                  text: responseText,
                  requestBody: requestBody,
                  fileInfo: {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                  },
                })
              }
            }

            // Show user-friendly error message
            const userMessage =
              errorDetails?.userMessage ||
              errorMessage ||
              `Upload failed with status ${response.status}. Please check:\n1. Upload server is running\n2. File format is correct\n3. File size is within limits`
            throw new Error(userMessage)
          }

          // Parse successful response as JSON
          let result
          try {
            result = JSON.parse(responseText)
          } catch (e) {
            throw new Error('Invalid JSON response from upload server')
          }

          if (!result || !result.uploadUri) {
            throw new Error('Invalid response from upload server: missing uploadUri')
          }

          if (!result.downloadUri) {
            throw new Error('Invalid response from upload server: missing downloadUri')
          }

          await axios
            .put(result.uploadUri, file, {
              headers: {
                'Content-Type': `${ext}`,
                'x-goog-acl': 'public-read',
              },
            })
            .then(async () => {
              try {
                await createEventWithLogo(result.downloadUri)
              } catch (error) {
                console.error('Error creating event:', error)
                const errorMessage =
                  error.message ||
                  error.graphQLErrors?.[0]?.message ||
                  'Failed to create event. Please try again.'
                showError(errorMessage, 'Create Event Failed')
              }
            })
            .catch((error) => {
              console.error('Error uploading file to storage:', error)
              const errorMessage =
                error.response?.data?.message ||
                error.message ||
                'Failed to upload file to storage. Please try again.'
              showError(errorMessage, 'Upload Failed')
            })
        } catch (error) {
          console.error('Error in file upload process:', error)
          const errorMessage = error.message || 'Failed to process file upload'

          // Check if it's a server error (500)
          if (errorMessage.includes('500') || errorMessage.includes('Internal Server Error')) {
            showError(
              `Server Error (500): The upload server encountered an internal error. Please check server logs and configuration.`,
              'Server Error',
              8000
            )
          } else {
            showError(
              `Upload Error: ${errorMessage}. Please check upload server, file format, and file size.`,
              'Upload Failed',
              8000
            )
          }
        }
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error)
      showError(error.message || 'Failed to submit form. Please try again.', 'Submit Failed')
    }
  }

  const selectFile = async (e) => {
    e.preventDefault()
    const tempFile = e.currentTarget.files[0]
    setFile(tempFile)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && droppedFile.type.startsWith('image/')) {
      setFile(droppedFile)
    }
  }

  if (user.role !== 'Admin') {
    return 'Cannot Access'
  }

  const handleStartDate = (e) => {
    const dateStart = e.target.value
    if (moment(dateStart).isAfter(endDate)) {
      setTimeDate(true)
    } else {
      setTimeDate(false)
    }
    setStartDate(dateStart)
  }

  const handleEndDate = (e) => {
    const dateEnd = e.target.value
    if (moment(dateEnd).isBefore(startDate)) {
      setTimeDate(true)
    } else {
      setTimeDate(false)
    }
    setEndDate(dateEnd)
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        pb: { xs: 3, sm: 4, md: 5 },
      }}>
      <form ref={formRef} onSubmit={handleSubmit}>
        {/* Content wrapper with padding for fixed button */}
        <Box sx={{ pb: { xs: 10, sm: 11, md: 12 } }}>
          {/* Page Header */}
          <Box sx={{ mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                mb: { xs: 0.25, sm: 0.375, md: 0.5 },
                mt: { xs: 1.25, sm: 1.5, md: 2 },
                color: 'text.primary',
                fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.75rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}>
              Create New Event
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
                lineHeight: 1.5,
              }}>
              Fill in the details below to create a new event
            </Typography>
          </Box>

          {/* Event Details Section */}
          <EventDetailsForm
            eventNameEN={eventNameEN}
            organizer={organizer}
            slugEvent={slugEvent}
            codeEvent={codeEvent}
            file={file}
            organizersData={data}
            onEventNameChange={(e) => setEventNameEN(e.target.value)}
            onOrganizerChange={(e) => setOrganizer(e.target.value)}
            onSlugChange={(e) => setSlugEvent(e.target.value)}
            onCodeChange={(e) => setCodeEvent(e.target.value)}
            onFileSelect={selectFile}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          />

          {/* Shirt Section */}
          <ShirtSection
            shirts={shirts}
            slugShirt={slugShirt}
            typeShirt={typeShirt}
            imageShirt={imageShirt}
            onSlugChange={(e) => setSlugShirt(e.target.value)}
            onTypeChange={(e) => setTypeShirt(e.target.value)}
            onImageChange={(e) => setImageShirt(e.target.value)}
            onAdd={addShirt}
            onRemove={removeShirt}
          />

          {/* Ticket Section */}
          <TicketSection
            tickets={tickets}
            ticketType={ticketType}
            startDate={startDate}
            endDate={endDate}
            timeDateError={timeDate}
            onTicketTypeChange={(e) => setTicketType(e.target.value)}
            onStartDateChange={handleStartDate}
            onEndDateChange={handleEndDate}
            onAdd={addTicketType}
            onRemove={removeTicket}
          />

          {/* Feature Toggles Section */}
          <FeatureToggles
            print={print}
            QRSearch={QRSearch}
            IDSearch={IDSearch}
            uploadFace={uploadFace}
            isFaceCheckin={isFaceCheckin}
            IDFace={IDFace}
            selfCheckin={selfCheckin}
            isShirtImage={isShirtImage}
            autoPickup={autoPickup}
            onPrintChange={(e) => setPrint(e.target.checked)}
            onQRSearchChange={(e) => setQRSearch(e.target.checked)}
            onIDSearchChange={(e) => setIDSearch(e.target.checked)}
            onUploadFaceChange={(e) => setUploadFace(e.target.checked)}
            onIsFaceCheckinChange={(e) => setIsFaceCheckin(e.target.checked)}
            onIDFaceChange={(e) => setIDFace(e.target.checked)}
            onSelfCheckinChange={(e) => setSelfCheckin(e.target.checked)}
            onIsShirtImageChange={(e) => setIsShirtImage(e.target.checked)}
            onAutoPickupChange={(e) => setAutoPickup(e.target.checked)}
          />
        </Box>
      </form>

      {/* Fixed Submit Button - Full Width */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          p: { xs: 1.5, sm: 2, md: 2.5 },
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.98)' : 'rgba(18, 18, 18, 0.98)',
          backdropFilter: 'blur(10px)',
          borderTop: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? '0 -2px 8px rgba(0, 0, 0, 0.1)'
              : '0 -2px 8px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
        }}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            if (formRef.current) {
              formRef.current.requestSubmit()
            }
          }}
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
            textTransform: 'none',
            py: { xs: 0.875, sm: 1, md: 1.25 },
            px: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 2px 6px rgba(52, 103, 246, 0.3)'
                : '0 2px 6px rgba(144, 202, 249, 0.4)',
            transition: (theme) =>
              theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
                duration: theme.transitions.duration.short,
              }),
            '&:hover': {
              backgroundColor: 'primary.dark',
              boxShadow: (theme) =>
                theme.palette.mode === 'light'
                  ? '0 3px 10px rgba(52, 103, 246, 0.4)'
                  : '0 3px 10px rgba(144, 202, 249, 0.5)',
              transform: 'translateY(-1px)',
            },
          }}>
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default CreateEventPage
